/**
 * Converts a string to a URL-friendly slug
 * @param {string} text - The text to slugify
 * @returns {string} - URL-friendly slug
 */
export const slugify = (text) => {
  return text
    .toString()
    .toLowerCase()
    .trim()
    .replace(/\s+/g, '-')           // Replace spaces with -
    .replace(/[^\w-]+/g, '')        // Remove all non-word chars
    .replace(/--+/g, '-')           // Replace multiple - with single -
    .replace(/^-+/, '')             // Trim - from start of text
    .replace(/-+$/, '');            // Trim - from end of text
};

/**
 * Converts a slug back to a more readable format
 * @param {string} slug - The slug to unslugify
 * @returns {string} - More readable text
 */
export const unslugify = (slug) => {
  return slug
    .replace(/-/g, ' ')
    .replace(/\w\S*/g, (txt) => 
      txt.charAt(0).toUpperCase() + txt.substr(1).toLowerCase()
    );
};

/**
 * Generate URL path for an opening and line
 * @param {object} opening - Opening object with name
 * @param {object} line - Line object with name  
 * @returns {string} - URL path like "/sicilian-defense/najdorf-variation"
 */
export const generateLineUrl = (opening, line) => {
  const openingSlug = slugify(opening.name);
  const lineSlug = slugify(line.name);
  return `/${openingSlug}/${lineSlug}`;
};

/**
 * Parse URL path to extract opening and line slugs
 * @param {string} pathname - URL pathname like "/sicilian-defense/najdorf-variation"
 * @returns {object} - {openingSlug, lineSlug}
 */
export const parseLineUrl = (pathname) => {
  const parts = pathname.split('/').filter(Boolean);
  
  if (parts.length >= 2) {
    return {
      openingSlug: parts[0],
      lineSlug: parts[1]
    };
  }
  
  return {
    openingSlug: null,
    lineSlug: null
  };
};

/**
 * Find opening and line by their slugs
 * @param {array} openings - Array of openings with lines
 * @param {string} openingSlug - Opening slug
 * @param {string} lineSlug - Line slug
 * @returns {object} - {opening, line} or {null, null}
 */
export const findBySlug = (openings, openingSlug, lineSlug) => {
  if (!openingSlug || !openings) {
    return { opening: null, line: null };
  }
  
  const opening = openings.find(o => slugify(o.name) === openingSlug);
  
  if (!opening) {
    return { opening: null, line: null };
  }
  
  // If no lineSlug provided, just return the opening
  if (!lineSlug) {
    return { opening, line: null };
  }
  
  if (!opening.lines) {
    return { opening, line: null };
  }
  
  const line = opening.lines.find(l => slugify(l.name) === lineSlug);
  
  return { 
    opening: opening || null, 
    line: line || null 
  };
};